import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Calendar, Check, Instagram, Plus, MapPin, MessageSquare, Send, Users } from "lucide-react";
import { EntityCard, SectionTitle, SocialProof, chipCls, type ProofAvatar } from "@/components/kit";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  CIRCLE_COLS,
  useCircleChatLink,
  useCircleCohosts,
  isCircleHost,
  useCircleMembership,
  type CircleRow,
} from "./circles";
import { type HostProfile } from "@/components/CircleHosts";
import { telegramHref, withTelegramText } from "@/lib/social-links";
import { ShareCircleButton } from "@/components/ShareCircleButton";
import { storeCircleRef } from "@/lib/circle-invite";
import { formatEventDate, type EventRow } from "@/routes/events";
import { pluralPeople } from "@/lib/referral";
import { pluralParticipants } from "@/lib/plural";
import { CircleTeam } from "@/components/CircleExperts";
import { CircleManageSheet } from "@/components/CircleManageSheet";

export const Route = createFileRoute("/_authenticated/circles/$circleId")({
  head: () => ({
    meta: [
      { title: "Сообщество — TurkeyDrive" },
      {
        name: "description",
        content: "Тематическое сообщество TurkeyDrive: встречи, запросы и участники.",
      },
      { property: "og:title", content: "Сообщество — TurkeyDrive" },
      {
        property: "og:description",
        content:
          "Тематическое сообщество TurkeyDrive: ближайшие встречи, запросы участников и лидер.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): { manage?: "experts" } =>
    s.manage === "experts" ? { manage: "experts" } : {},
  component: CirclePage,
});

type MemberProfile = {
  user_id: string;
  full_name: string;
  photo_url: string | null;
};

type CircleEvent = Pick<
  EventRow,
  | "id"
  | "title"
  | "event_date"
  | "event_end_date"
  | "city"
  | "cover_image_url"
  | "topics"
  | "status"
>;

function CirclePage() {
  const { circleId } = Route.useParams();
  const search = Route.useSearch();
  const { uid, join, leave } = useCircleMembership();
  const qc = useQueryClient();

  // Реферальная ссылка: /circles/<id>?ref=<user_id>
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const ref = new URL(window.location.href).searchParams.get("ref");
      if (ref) storeCircleRef(circleId, ref);
    } catch {
      /* ignore */
    }
  }, [circleId]);

  const { data: circle, isLoading } = useQuery({
    queryKey: ["circle", circleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circles")
        .select(CIRCLE_COLS)
        .eq("id", circleId)
        .eq("is_deleted", false)
        .maybeSingle();
      if (error) throw error;
      return (data as CircleRow | null) ?? null;
    },
  });

  const { data: memberIds = [] } = useQuery({
    queryKey: ["circle-members", circleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_members")
        .select("user_id")
        .eq("circle_id", circleId);
      if (error) throw error;
      return (data ?? []).map((r) => r.user_id as string);
    },
  });

  const { data: memberProfiles = [] } = useQuery({
    queryKey: ["circle-member-profiles", circleId, memberIds.length],
    enabled: memberIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url")
        .in("user_id", memberIds.slice(0, 50));
      if (error) throw error;
      return (data ?? []) as MemberProfile[];
    },
  });

  const { data: invitedCount = 0 } = useQuery({
    queryKey: ["circle-invited-count", circleId, uid],
    enabled: !!uid,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("circle_members")
        .select("id", { count: "exact", head: true })
        .eq("circle_id", circleId)
        .eq("invited_by", uid!);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: host } = useQuery({
    queryKey: ["circle-host", circle?.host_user_id],
    enabled: !!circle?.host_user_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, telegram")
        .eq("user_id", circle!.host_user_id!)
        .maybeSingle();
      if (error) throw error;
      return (data as (MemberProfile & { telegram: string | null }) | null) ?? null;
    },
  });

  const { data: cohostRows = [] } = useCircleCohosts(circle ? [circle.id] : []);
  const cohostIds = cohostRows.map((h) => h.user_id);
  const { data: cohostProfiles = [] } = useQuery({
    queryKey: ["circle-cohost-profiles", circleId, cohostIds.join(",")],
    enabled: cohostIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_id, full_name, photo_url, city")
        .in("user_id", cohostIds);
      if (error) throw error;
      const rows = (data ?? []) as HostProfile[];
      return cohostIds
        .map((id) => rows.find((r) => r.user_id === id))
        .filter((r): r is HostProfile => !!r);
    },
  });
  const allHosts: HostProfile[] = [...(host ? [host] : []), ...cohostProfiles];

  const topics = circle?.topics ?? [];
  const isHost = !!circle && isCircleHost(circle, uid, cohostRows);
  const isCreator = !!uid && circle?.host_user_id === uid;

  const { data: isAdmin = false } = useQuery({
    queryKey: ["is-admin", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", uid!)
        .maybeSingle();
      return Boolean(data?.is_admin);
    },
  });

  // Только явно привязанные мероприятия — никакого авто-подбора по темам.
  const { data: events = [] } = useQuery({
    queryKey: ["circle-events", circleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, event_date, event_end_date, city, cover_image_url, topics, status")
        .eq("circle_id", circleId)
        .eq("status", "published")
        .order("event_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CircleEvent[];
    },
  });

  const { data: requests = [] } = useQuery({
    queryKey: ["circle-requests", circleId, memberIds.length],
    enabled: memberIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("requests")
        .select("id, title, description, city, category, user_id")
        .eq("status", "published")
        .in("user_id", memberIds.slice(0, 50))
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as {
        id: string;
        title: string;
        description: string | null;
        city: string | null;
        category: string;
        user_id: string;
      }[];
    },
  });

  const canSeeChat = (!!uid && memberIds.includes(uid)) || isHost || isAdmin;
  const { data: chatLink } = useCircleChatLink(circleId, canSeeChat && !!circle?.has_chat);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-md px-4 py-10 text-sm text-td-muted">
        Загружаем сообщество…
      </div>
    );
  }

  if (!circle) {
    return (
      <div className="mx-auto w-full max-w-md px-4 py-10">
        <p className="text-sm text-td-muted">Сообщество не найдено.</p>
        <Link to="/circles" className="mt-3 inline-block text-sm font-semibold text-td-teal">
          ← Все сообщества
        </Link>
      </div>
    );
  }

  const isMember = !!uid && memberIds.includes(uid);
  const now = Date.now();
  const upcomingEvents = events
    .filter((event) => {
      const effectiveDate = event.event_end_date ?? event.event_date;
      return effectiveDate ? new Date(effectiveDate).getTime() >= now : false;
    })
    .slice(0, 3);
  const pastEvents = events
    .filter((event) => {
      const effectiveDate = event.event_end_date ?? event.event_date;
      return effectiveDate ? new Date(effectiveDate).getTime() < now : false;
    })
    .sort((a, b) => new Date(b.event_date ?? 0).getTime() - new Date(a.event_date ?? 0).getTime())
    .slice(0, 2);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ["circle", circleId] });
    await qc.invalidateQueries({ queryKey: ["circle-events", circleId] });
    await qc.invalidateQueries({ queryKey: ["circles"] });
  };

  const memberAvatars: ProofAvatar[] = memberProfiles.slice(0, 5).map((m) => ({
    photo_url: m.photo_url,
    initial: (m.full_name || "?").slice(0, 1).toUpperCase(),
  }));
  const showEvents = events.length > 0 || isHost || isAdmin;

  return (
    <div className="mx-auto w-full min-w-0 max-w-md px-4 pb-28 pt-5">
      <Link to="/circles" className="text-sm font-medium text-td-muted">
        ← Сообщества
      </Link>

      <header className="mt-3">
        <div className="flex w-full items-center justify-center overflow-hidden rounded-2xl bg-td-soft py-6">
          {circle.logo_url ? (
            <img
              decoding="async"
              src={circle.logo_url}
              alt={circle.name}
              className="h-40 w-40 rounded-2xl object-cover"
            />
          ) : (
            <span className="flex h-40 w-40 items-center justify-center text-7xl">
              {circle.cover}
            </span>
          )}
        </div>
        <h1 className="mt-4 break-words font-serif text-[28px] leading-9 text-td-dark [overflow-wrap:anywhere]">
          {circle.name}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
          <span className="inline-flex items-center gap-1 font-medium text-td-teal">
            <MapPin className="h-3.5 w-3.5" />
            {circle.city ?? "Вся Турция"}
          </span>
          <span className="inline-flex items-center gap-1 text-td-muted">
            <Users className="h-3.5 w-3.5" />
            {pluralParticipants(memberIds.length)}
          </span>
        </div>
        <div className="mt-4">
          {!isHost && !isMember ? (
            <button
              type="button"
              onClick={() => join(circle.id)}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-td-teal text-base font-semibold text-white hover:bg-td-teal/90"
            >
              Вступить в сообщество
            </button>
          ) : isMember ? (
            <span className="inline-flex h-12 w-full items-center justify-center gap-1.5 rounded-full bg-td-soft text-base font-semibold text-td-dark">
              Ты в сообществе <Check className="h-5 w-5 text-td-teal" />
            </span>
          ) : null}
          {!canSeeChat && circle.has_chat && (
            <p className="mt-2 text-center text-[13px] text-td-muted">
              У сообщества есть чат — он откроется после вступления
            </p>
          )}
        </div>
      </header>
      {(isHost || isAdmin) && (
        <CircleManageSheet
          circle={circle}
          events={events}
          onChanged={invalidate}
          creator={host ?? null}
          cohosts={cohostProfiles}
          isCreator={isCreator}
          uid={uid ?? null}
          memberIds={memberIds}
          manageExperts={search.manage === "experts"}
        />
      )}

      {circle.description && (
        <section className="mt-6">
          <SectionTitle>О сообществе</SectionTitle>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-td-dark">
            {circle.description}
          </p>
        </section>
      )}

      {showEvents && (
        <section className="mt-6">
          <SectionTitle>Ближайшие встречи</SectionTitle>
          {events.length === 0 ? (
            <div className="mt-2 rounded-2xl border border-td-line bg-white p-4">
              <p className="text-sm text-td-muted">У сообщества пока нет встреч</p>
              <Button
                asChild
                variant="outline"
                className="mt-3 h-11 w-full rounded-full border-td-teal text-td-teal hover:bg-td-teal/10 hover:text-td-teal"
              >
                <Link to="/events/new">
                  <Plus className="mr-1.5 h-4 w-4" />
                  Создать встречу
                </Link>
              </Button>
            </div>
          ) : (
            <div className="mt-2 space-y-3">
              {upcomingEvents.length > 0 && <EventList events={upcomingEvents} />}
              {pastEvents.length > 0 && (
                <div>
                  <h3 className="mb-2 font-sans text-[15px] font-semibold text-td-dark">
                    Уже прошли
                  </h3>
                  <EventList events={pastEvents} past />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <CircleTeam
        circleId={circle.id}
        hosts={allHosts}
        hostNote={circle.host_note}
        uid={uid ?? null}
        isHost={isHost}
        isMember={isMember}
      />

      <section className="mt-6">
        <SectionTitle>Участники</SectionTitle>
        <div className="mt-2 flex min-w-0 items-center justify-between gap-3 rounded-2xl border border-td-line bg-white p-3">
          {memberIds.length > 0 ? (
            <SocialProof
              count={memberIds.length}
              label={pluralParticipants(memberIds.length)}
              avatars={memberAvatars}
            />
          ) : (
            <span className="text-[13px] text-td-muted">Пока никого</span>
          )}
          {memberProfiles.length > 0 && (
            <Sheet>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-11 shrink-0 items-center rounded-full border border-td-teal px-4 text-[13px] font-semibold text-td-teal hover:bg-td-teal/10"
                >
                  Все участники
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
                <SheetHeader className="text-left">
                  <SheetTitle className="font-sans text-[17px] font-semibold text-td-dark">
                    Участники — {pluralParticipants(memberIds.length)}
                  </SheetTitle>
                </SheetHeader>
                <ul className="mt-3 space-y-1">
                  {memberProfiles.map((m) => (
                    <li key={m.user_id}>
                      <Link
                        to="/people/$userId"
                        params={{ userId: m.user_id }}
                        className="flex min-h-11 items-center gap-3 rounded-xl px-2 hover:bg-td-soft"
                      >
                        {m.photo_url ? (
                          <img
                            loading="lazy"
                            src={m.photo_url}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover"
                          />
                        ) : (
                          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-td-soft text-xs font-semibold text-td-dark">
                            {m.full_name.slice(0, 1)}
                          </span>
                        )}
                        <span className="min-w-0 truncate text-sm text-td-dark">{m.full_name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </section>

      {topics.length > 0 && (
        <section className="mt-6">
          <SectionTitle>Темы</SectionTitle>
          <div className="mt-2 flex flex-wrap gap-2">
            {topics.map((topic) => (
              <span key={topic} className={chipCls(false)}>
                {topic}
              </span>
            ))}
          </div>
        </section>
      )}

      {(circle.instagram_url || circle.telegram_url) && (
        <section className="mt-6">
          <SectionTitle>Где живёт сообщество</SectionTitle>
          <div className="mt-2 flex flex-wrap gap-2">
            {circle.instagram_url && (
              <Button asChild variant="outline" className="h-11 rounded-full">
                <a href={circle.instagram_url} target="_blank" rel="noopener noreferrer">
                  <Instagram className="mr-1.5 h-4 w-4" />
                  Инстаграм
                </a>
              </Button>
            )}
            {circle.telegram_url && (
              <Button asChild variant="outline" className="h-11 rounded-full">
                <a href={circle.telegram_url} target="_blank" rel="noopener noreferrer">
                  <Send className="mr-1.5 h-4 w-4" />
                  Телеграм-канал
                </a>
              </Button>
            )}
          </div>
        </section>
      )}

      {canSeeChat && circle.chat_access !== "closed" && chatLink && (
        <section className="mt-6">
          <SectionTitle>Чат сообщества</SectionTitle>
          <Button asChild variant="outline" className="mt-2 h-11 rounded-full">
            <a href={chatLink} target="_blank" rel="noopener noreferrer">
              <MessageSquare className="mr-1.5 h-4 w-4" />
              Чат сообщества
            </a>
          </Button>
          {isAdmin && !isMember && !isHost && (
            <p className="mt-2 text-[13px] text-td-muted">Видно вам как админу</p>
          )}
        </section>
      )}

      {circle.chat_access === "closed" &&
        isMember &&
        !isHost &&
        (() => {
          const tg = telegramHref(host?.telegram);
          const text = `Здравствуйте! Я из TurkeyDrive, вступил(а) в ваше сообщество «${circle.name}» и хочу попасть в чат сообщества. Мой профиль: https://turkey-drive.com/people/${uid}`;
          return (
            <section className="mt-6">
              <SectionTitle>Чат сообщества</SectionTitle>
              {tg ? (
                <a
                  href={withTelegramText(tg, text)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-td-teal px-5 text-sm font-semibold text-white hover:brightness-105"
                >
                  <MessageSquare className="h-4 w-4" />
                  Попросить доступ в чат
                </a>
              ) : (
                <Button
                  asChild
                  variant="outline"
                  className="mt-2 h-11 w-full rounded-full border-td-teal text-td-teal hover:bg-td-teal/10 hover:text-td-teal"
                >
                  <Link to="/people/$userId" params={{ userId: circle.host_user_id ?? "" }}>
                    Написать лидеру
                  </Link>
                </Button>
              )}
            </section>
          );
        })()}

      {(isMember || isHost) && (
        <section className="mt-6 space-y-2">
          <ShareCircleButton circleId={circle.id} name={circle.name} />
          {invitedCount > 0 && (
            <p className="text-center text-[13px] text-td-muted">
              Ты привёл(а) {invitedCount} {pluralPeople(invitedCount)} в это сообщество
            </p>
          )}
          <InstantNotifyToggle circleId={circle.id} />
        </section>
      )}

      {requests.length > 0 && (
        <section className="mt-6">
          <SectionTitle>Запросы участников</SectionTitle>
          <div className="mt-2 flex flex-col gap-3">
            {requests.map((r) => (
              <Link
                key={r.id}
                to="/requests"
                className="block rounded-2xl border border-td-line bg-white p-3"
              >
                <span className="block min-w-0 font-sans text-base font-semibold text-td-dark">
                  {r.title}
                </span>
                <p className="mt-1 line-clamp-2 text-[13px] text-td-muted">
                  {r.category} · {r.city ?? "Вся Турция"}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {isMember && !isHost && (
        <section className="mt-8 flex justify-center pb-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="min-h-11 text-[13px] text-td-muted underline-offset-2 hover:underline">
                Выйти из сообщества
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-sm rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Выйти из сообщества?</AlertDialogTitle>
                <AlertDialogDescription>
                  Вы перестанете получать уведомления о встречах сообщества и потеряете доступ к
                  чату
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Отмена</AlertDialogCancel>
                <AlertDialogAction onClick={() => leave(circle.id)}>Выйти</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      )}
    </div>
  );
}

function EventList({ events, past }: { events: CircleEvent[]; past?: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-3">
      {events.map((event) => (
        <EntityCard
          key={event.id}
          dimmed={past}
          onOpen={() => void navigate({ to: "/events", search: { event: event.id } })}
          image={
            event.cover_image_url ? (
              <img
                loading="lazy"
                decoding="async"
                src={event.cover_image_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center">
                <Calendar className="h-6 w-6 text-td-muted" />
              </span>
            )
          }
          meta={<span className="min-w-0 truncate">{formatEventDate(event.event_date)}</span>}
          title={event.title}
          place={event.city}
        />
      ))}
    </div>
  );
}

/** Тумблер мгновенных писем о новых встречах круга (только для участников). */
function InstantNotifyToggle({ circleId }: { circleId: string }) {
  const qc = useQueryClient();
  const { data: membership } = useQuery({
    queryKey: ["circle-notify-instant", circleId],
    queryFn: async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) return null;
      const { data, error } = await supabase
        .from("circle_members")
        .select("id, notify_instant")
        .eq("circle_id", circleId)
        .eq("user_id", uid)
        .maybeSingle();
      if (error) throw error;
      return (data as { id: string; notify_instant: boolean } | null) ?? null;
    },
  });

  const toggle = useMutation({
    mutationFn: async (value: boolean) => {
      if (!membership) return;
      const { error } = await supabase
        .from("circle_members")
        .update({ notify_instant: value })
        .eq("id", membership.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["circle-notify-instant", circleId] });
      toast.success("Настройка сохранена");
    },
    onError: () => toast.error("Не удалось сохранить настройку"),
  });

  if (!membership) return null;

  return (
    <label className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-xl bg-td-soft/70 px-3 py-2">
      <span className="text-xs text-td-dark">
        Уведомлять сразу о новых встречах в этом сообществе
      </span>
      <Switch
        checked={membership.notify_instant}
        onCheckedChange={(v) => toggle.mutate(v)}
        disabled={toggle.isPending}
      />
    </label>
  );
}
